/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["focused"],
  _excluded2 = ["ariaLiveRegionId", "maxLength", "value", "focused"],
  _excluded3 = ["aria-describedby", "affordance", "affordanceClassName", "className", "error", "inputClassName", "inputRef", "minWidth", "onBlur", "onClick", "onFocus", "onMouseEnter", "onMouseLeave", "onMouseMove", "readOnly", "use", "maxLength", "value"],
  _excluded4 = ["hovered"],
  _excluded5 = ["focused"];
import classNames from 'classnames';
import { BATTLESHIP, CALYPSO, EERIE, GYPSUM, HEFFALUMP, OLAF } from 'HubStyleTokens/colors';
import { INPUT_FONT_SIZE } from 'HubStyleTokens/sizes';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import styled, { css } from 'styled-components';
import { callIfPossible, wrapRefObject } from '../core/Functions';
import Controllable from '../decorators/Controllable';
import ShareInput from '../decorators/ShareInput';
import UIIcon from '../icon/UIIcon';
import FocusProvider from '../providers/FocusProvider';
import HoverProvider from '../providers/HoverProvider';
import { fontsLoaded, fontsLoadedPromise } from '../utils/Fonts';
import { getTextWidth } from '../utils/MeasureText';
import { hidden } from '../utils/propTypes/decorators';
import refObject from '../utils/propTypes/refObject';
import { getPlaceholderStyles, setUiTransition, uiFocus } from '../utils/Styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const INPUT_MAX_LENGTH_WARNING_THRESHOLD = 15;
const getStyle = inputWidth => ({
  width: inputWidth
});
const placeholderStyles = getPlaceholderStyles(css(["color:", ";"], ({
  use
}) => use === 'on-dark' ? BATTLESHIP : EERIE));
const computeBackgroundColor = ({
  focused,
  hovered,
  use
}) => {
  if (!focused && !hovered) return 'transparent';
  return use === 'on-dark' ? HEFFALUMP : GYPSUM;
};
const Wrapper = styled.div.withConfig({
  displayName: "UIAutosizedTextInput__Wrapper",
  componentId: "sc-1jddsq8-0"
})(["display:inline-block;position:relative;max-width:100%;color:", ";"], ({
  use
}) => use === 'on-dark' && OLAF);
const Input = styled.input.withConfig({
  displayName: "UIAutosizedTextInput__Input",
  componentId: "sc-1jddsq8-1"
})(["background-color:transparent;border:0;box-sizing:content-box;color:inherit;cursor:text;max-width:100%;outline:", ";&:not(:empty){display:inline-block;}", ";"], ({
  affordance
}) => affordance && 0, placeholderStyles);
const Affordance = styled.div.withConfig({
  displayName: "UIAutosizedTextInput__Affordance",
  componentId: "sc-1jddsq8-2"
})(["", ""], ({
  show
}) => show && css(["display:inline-block;margin:-4px 0 -4px -4px;max-width:calc(100% + 4px);padding:4px 38px 4px 4px;background-color:", ";", ";"], computeBackgroundColor, ({
  focused
}) => focused && uiFocus));
const Icon = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(UIIcon, Object.assign({
    name: "edit",
    size: "small"
  }, rest));
}).withConfig({
  displayName: "UIAutosizedTextInput__Icon",
  componentId: "sc-1jddsq8-3"
})(["position:absolute;top:50%;right:10px;transform:translateY(-50%);font-size:", ";color:", ";pointer-events:none;opacity:", ";", ";"], INPUT_FONT_SIZE, ({
  use
}) => use === 'on-dark' ? OLAF : CALYPSO, ({
  focused
}) => focused ? 0 : 1, setUiTransition());
const getCharacterCounterOpacity = ({
  focused,
  maxLength,
  value = ''
}) => focused && maxLength - value.length <= INPUT_MAX_LENGTH_WARNING_THRESHOLD ? 1 : 0;
const CharacterCounter = styled(props => {
  const {
      ariaLiveRegionId,
      maxLength,
      value = '',
      focused
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded2);
  if (!maxLength) return null;
  const charactersRemaining = maxLength - value.length;

  // Render hidden text when approaching the threshold
  if (charactersRemaining > INPUT_MAX_LENGTH_WARNING_THRESHOLD + 1) {
    return null;
  }
  const displayedCharactersRemaining = charactersRemaining > INPUT_MAX_LENGTH_WARNING_THRESHOLD ? INPUT_MAX_LENGTH_WARNING_THRESHOLD : charactersRemaining;
  return /*#__PURE__*/_jsxs("div", Object.assign({}, rest, {
    children: [/*#__PURE__*/_jsx("span", {
      "aria-hidden": true,
      children: displayedCharactersRemaining
    }), getCharacterCounterOpacity({
      focused,
      maxLength,
      value
    }) === 1 && /*#__PURE__*/_jsx("span", {
      id: ariaLiveRegionId,
      className: "sr-only",
      "aria-live": "polite",
      "aria-atomic": true,
      children: I18n.text('ui.autosizedTextInput.charactersRemaining', {
        value: charactersRemaining
      })
    })]
  }));
}).withConfig({
  displayName: "UIAutosizedTextInput__CharacterCounter",
  componentId: "sc-1jddsq8-4"
})(["position:absolute;top:50%;right:12px;transform:translateY(-50%);font-size:", ";color:", ";pointer-events:none;opacity:", ";", ";"], INPUT_FONT_SIZE, ({
  use
}) => use === 'on-dark' ? BATTLESHIP : EERIE, getCharacterCounterOpacity, setUiTransition());
let uuid = 0;
class UIAutosizedTextInput extends PureComponent {
  constructor(props) {
    super(props);
    this.handleClick = evt => {
      const {
        inputRef,
        onClick
      } = this.props;
      callIfPossible(onClick, evt);
      if (!evt.preventPrevented) {
        if (inputRef && inputRef.current) inputRef.current.focus();
      }
    };
    this.state = {
      inputWidth: props.minWidth
    };
    this._getStyle = memoizeOne(getStyle);
    this._ariaLiveRegionId = `uiautosizedtextinput-maxlength-warning-${uuid++}`;
  }
  componentDidMount() {
    this.measureWidth();

    // Initial width calculation may break when fonts become ready
    if (!fontsLoaded()) {
      fontsLoadedPromise.then(() => {
        this.measureWidth();
      });
    }
  }
  componentDidUpdate(prevProps, prevState) {
    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.inputWidth === this.state.inputWidth) {
      this.measureWidth();
    }
  }
  measureWidth() {
    const {
      inputRef,
      minWidth
    } = this.props;
    if (!inputRef.current) return;
    const newInputWidth = Math.ceil(Math.max(minWidth || 0, getTextWidth(inputRef.current)));
    if (this.state.inputWidth !== newInputWidth) {
      this.setState({
        inputWidth: newInputWidth
      });
    }
  }
  render() {
    const _this$props = this.props,
      {
        'aria-describedby': ariaDescribedBy,
        affordance,
        affordanceClassName,
        className,
        error,
        inputClassName,
        inputRef,
        readOnly,
        use,
        maxLength,
        value
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded3);
    return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, this.props, {
      children: hoverProviderProps => /*#__PURE__*/_jsx(FocusProvider, Object.assign({}, this.props, {
        children: focusProviderProps => {
          const {
              hovered
            } = hoverProviderProps,
            hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded4);
          const {
              focused
            } = focusProviderProps,
            focusProviderRestProps = _objectWithoutPropertiesLoose(focusProviderProps, _excluded5);
          const {
            inputWidth
          } = this.state;
          const hasIcon = affordance && !readOnly;
          return /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, hoverProviderRestProps, focusProviderRestProps, {
            className: classNames('private-expanding-text-input', className),
            focused: focused,
            hovered: hovered,
            onClick: this.handleClick,
            use: use,
            children: [/*#__PURE__*/_jsx(Affordance, {
              className: affordance ? classNames(affordanceClassName, error && 'private-form__control--error') : '',
              focused: focused,
              hovered: hovered,
              show: affordance,
              use: use,
              children: /*#__PURE__*/_jsx(Input, Object.assign({}, rest, {
                "aria-invalid": error,
                "aria-describedby": classNames(ariaDescribedBy, this._ariaLiveRegionId),
                value: value,
                maxLength: maxLength,
                affordance: affordance,
                className: inputClassName,
                focused: focused,
                hovered: hovered,
                readOnly: readOnly,
                ref: ref => {
                  wrapRefObject(inputRef)(ref);
                },
                style: this._getStyle(inputWidth),
                use: use
              }))
            }), hasIcon && /*#__PURE__*/_jsx(CharacterCounter, {
              value: value,
              maxLength: maxLength,
              use: use,
              focused: focused,
              ariaLiveRegionId: this._ariaLiveRegionId
            }), hasIcon && /*#__PURE__*/_jsx(Icon, {
              focused: focused,
              use: use
            })]
          }));
        }
      }))
    }));
  }
}
UIAutosizedTextInput.propTypes = {
  value: PropTypes.string,
  maxLength: PropTypes.number,
  error: PropTypes.bool,
  focused: hidden(PropTypes.bool),
  hovered: hidden(PropTypes.bool),
  minWidth: PropTypes.number,
  /**
   * Determines whether or not editor controls and error states are shown to the user.
   * When `false`, the app must signal to the user that this text is editable, and
   * handle possible error states.
   */
  affordance: PropTypes.bool,
  affordanceClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  inputRef: refObject,
  readOnly: PropTypes.bool,
  use: PropTypes.oneOf(['default', 'on-dark']).isRequired,
  'aria-describedby': hidden(PropTypes.string)
};
UIAutosizedTextInput.defaultProps = {
  affordance: false,
  inputClassName: '',
  use: 'default'
};
UIAutosizedTextInput.displayName = 'UIAutosizedTextInput';
export default ShareInput(Controllable(UIAutosizedTextInput));